/* eslint-disable no-unused-vars */
import React, {
    useContext, useEffect, useMemo, useState
} from 'react';
import {
    AlertCircle, Loader2, Search, X
} from 'lucide-react';
import {
    Context
} from 'shared/context/GlobalState';
import {
    listCommonOrgBlueprints, deployBlueprint
} from '../services/blueprintDeepCopy';
import {
    getBlueprintVisual
} from './blueprintVisual';
import {
    Button
} from 'shared/components/ui/button';
import './BlueprintModal.css';

/**
 * BlueprintModal — "+ New Assistant" deploy entry point.
 * Per A-0012.02 SPEC §2.5 + UF-11 + UF-15.
 */
const BLANK_BLUEPRINT = {
    assistant_id: '__blank__',
    title: 'Blank assistant',
    description: 'Start with an empty assistant. Add behaviors, voice, and channels yourself.',
    __isBlank: true
};

function matchesBlueprint(bp, query) {
    if (!query) return true;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
        bp.title,
        bp.description,
        bp.assistant_id,
        bp.__isBlank ? 'blank fresh empty start' : '',
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
}

export default function BlueprintModal({
    open, onClose, userOrg, userSub, onDeployed
}) {
    const {
        user
    } = useContext(Context);
    const [blueprints, setBlueprints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [selected, setSelected] = useState(BLANK_BLUEPRINT);
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const [deploying, setDeploying] = useState(false);
    const [deployResult, setDeployResult] = useState(null);
    const [blueprintSearch, setBlueprintSearch] = useState('');

    useEffect(() => {
        if (!open) return;
        setSelected(BLANK_BLUEPRINT);
        setName('');
        setNameError('');
        setBlueprintSearch('');
        setDeployResult(null);
        setLoading(true);
        setLoadError(null);
        listCommonOrgBlueprints()
            .then((list) => {
                setBlueprints(list);
                if (list.length === 0) {
                    setLoadError('Common Org returned 0 blueprints. Blank is available below.');
                }
            })
            .catch((err) => {
                setLoadError(err?.message || 'Could not load blueprints from Common Org.');
            })
            .finally(() => setLoading(false));
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const filteredBlueprints = useMemo(
        () => blueprints.filter((bp) => matchesBlueprint(bp, blueprintSearch)),
        [blueprints, blueprintSearch],
    );
    const showBlank = matchesBlueprint(BLANK_BLUEPRINT, blueprintSearch);
    const hasVisibleOptions = showBlank || filteredBlueprints.length > 0;
    const trimmedName = name.trim();
    const canDeploy = Boolean(selected && trimmedName && !deploying);

    if (!open) return null;

    async function handleDeploy() {
        if (!selected) return;
        const trimmedName = name.trim();
        if (!trimmedName) {
            setNameError('Assistant name is required.');
            return;
        }
        setNameError('');
        setDeploying(true);
        setDeployResult(null);

        try {
            if (selected.__isBlank) {
                const {
                    opcodeAssistantService
                } = await import('shared/api');
                const blank = {
                    title: trimmedName,
                    description: 'Created from blank template.',
                    persona: '',
                    version: '0.1.0',
                    status: 'active',
                    disabled: false,
                    org_id: userOrg,
                    sub_org_id: userSub,
                    assistant_token: 'sk-ast-' + Math.random().toString(36).slice(2, 18) + Math.random().toString(36).slice(2, 10),
                    opcodes: [],
                    typed_input: {
                        context_keys: [],
                        propensity_keys: [],
                        intent: {
                            format: 'text',
                            routing_mode: 'default_only'
                        }
                    },
                    knowledge: [],
                    data_connections: [],
                    instructions: {
                        system_prompt: '',
                        guidelines: '',
                        content_policy: ''
                    },
                    channels: {
                        mode: 'open',
                        authorized_channels: []
                    },
                    memory: {
                        strategy: 'window',
                        max_turns: 10,
                        max_tokens: 4000,
                        ttl: 86400,
                        employee_memory: false
                    },
                    governance: {
                        allowed_actions: [],
                        denied_actions: [],
                        escalation: [],
                        boundaries: ''
                    }
                };
                try {
                    const res = await opcodeAssistantService.createOpcodeAssistant(blank);
                    const createdId = res?.data?.assistant_id;
                    if (!createdId) throw new Error('Server did not return new assistant_id.');
                    setDeployResult({
                        ok: true,
                        newAssistantId: createdId,
                        warnings: [],
                        errors: []
                    });
                    onDeployed?.(createdId, []);
                } catch (err) {
                    const detail = err?.response?.data?.message
                        || err?.response?.data?.error
                        || JSON.stringify(err?.response?.data || {})
                        || err?.message
                        || 'Create failed';
                    setDeployResult({
                        ok: false,
                        errors: [detail]
                    });
                }
            } else {
                const result = await deployBlueprint(
                    selected.assistant_id, userOrg, userSub, trimmedName, user?._id
                );
                setDeployResult({
                    ok: !!result.newAssistantId,
                    ...result
                });
                if (result.newAssistantId) {
                    onDeployed?.(result.newAssistantId, result.warnings || []);
                }
            }
        } finally {
            setDeploying(false);
        }
    }

    return (
        <div
            className="bp-modal-backdrop"
            role="presentation"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="bp-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="bp-modal-title"
            >
                <div className="bp-modal__header">
                    <div className="bp-modal__header-text">
                        <h2 id="bp-modal-title" className="bp-modal__title">
                            Deploy a new assistant
                        </h2>
                        <p className="bp-modal__desc">
                            Pick a blueprint or start blank. You can change everything after deploy.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="bp-modal__close"
                        onClick={() => onClose?.()}
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="bp-modal__body">
                    <section>
                        <p className="bp-modal__section-label">Choose a template</p>

                        <div className="bp-modal__search">
                            <Search size={14} aria-hidden="true" />
                            <input
                                type="search"
                                value={blueprintSearch}
                                onChange={(e) => setBlueprintSearch(e.target.value)}
                                placeholder="Search by name or description…"
                                aria-label="Search blueprints"
                            />
                        </div>

                        <div className="bp-modal__list-area">
                            {loading && (
                                <div
                                    className="bp-modal__list"
                                    aria-busy="true"
                                    aria-label="Loading blueprints"
                                >
                                    {[0, 1, 2, 3].map((i) => (
                                        <BlueprintCardSkeleton key={i} />
                                    ))}
                                </div>
                            )}

                            {!loading && loadError && (
                                <div className="bp-modal__notice">
                                    <AlertCircle size={14} className="flex-shrink-0" style={{
                                        marginTop: 1
                                    }} />
                                    <span>{loadError}</span>
                                </div>
                            )}

                            {!loading && hasVisibleOptions && (
                                <div className="bp-modal__list" role="listbox" aria-label="Blueprint templates">
                                    {showBlank && (
                                        <BlueprintCard
                                            bp={BLANK_BLUEPRINT}
                                            selected={selected?.assistant_id === BLANK_BLUEPRINT.assistant_id}
                                            onSelect={() => setSelected(BLANK_BLUEPRINT)}
                                        />
                                    )}
                                    {filteredBlueprints.map((bp) => (
                                        <BlueprintCard
                                            key={bp.assistant_id}
                                            bp={bp}
                                            selected={selected?.assistant_id === bp.assistant_id}
                                            onSelect={() => setSelected(bp)}
                                        />
                                    ))}
                                </div>
                            )}

                            {!loading && !hasVisibleOptions && blueprints.length > 0 && (
                                <p className="bp-modal__empty-search">No blueprints match your search.</p>
                            )}
                        </div>
                    </section>

                    <div className="bp-modal__field">
                        <label htmlFor="bp-name">
                            Assistant name
                            <span className="bp-modal__required" aria-hidden="true">*</span>
                        </label>
                        <input
                            id="bp-name"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                if (nameError) setNameError('');
                            }}
                            placeholder="Enter a name for this assistant"
                            required
                            aria-invalid={Boolean(nameError)}
                            aria-describedby={nameError ? 'bp-name-error' : undefined}
                            className={nameError ? 'bp-modal__field-input--error' : undefined}
                        />
                        {nameError && (
                            <p id="bp-name-error" className="bp-modal__field-error">{nameError}</p>
                        )}
                    </div>

                    {deployResult && (
                        <div className={`bp-modal__result ${deployResult.ok ? 'bp-modal__result--ok' : 'bp-modal__result--err'}`}>
                            <strong>{deployResult.ok ? 'Deployed successfully' : 'Deploy failed'}</strong>
                            {deployResult.warnings?.length > 0 && (
                                <ul>
                                    {deployResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                            )}
                            {deployResult.errors?.length > 0 && (
                                <ul>
                                    {deployResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            )}
                        </div>
                    )}
                </div>

                <div className="bp-modal__footer">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onClose?.()}
                        disabled={deploying}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleDeploy}
                        disabled={!canDeploy}
                    >
                        {deploying ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                                Deploying
                            </>
                        ) : 'Deploy'}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function BlueprintCard({
    bp, selected, onSelect
}) {
    const visual = getBlueprintVisual(bp);

    return (
        <button
            type="button"
            role="option"
            aria-selected={selected}
            onClick={onSelect}
            className={`bp-card${selected ? ' bp-card--selected' : ''}`}
        >
            <div className="bp-card__head">
                <BlueprintVisualBadge visual={visual} />
                <div className="bp-card__title">{bp.title || bp.assistant_id}</div>
            </div>
            <div className="bp-card__desc">{bp.description || 'No description provided.'}</div>
        </button>
    );
}

function BlueprintVisualBadge({
    visual, selected
}) {
    const {
        Icon
    } = visual;
    return (
        <span className="bp-card__icon" aria-hidden="true">
            <Icon size={15} />
        </span>
    );
}

function BlueprintCardSkeleton() {
    return (
        <div className="bp-card-skeleton" aria-hidden="true">
            <div className="bp-card-skeleton__head">
                <div className="bp-card-skeleton__icon animate-pulse" />
                <div className="bp-card-skeleton__line bp-card-skeleton__line--title animate-pulse" />
            </div>
            <div className="bp-card-skeleton__line bp-card-skeleton__line--desc animate-pulse" />
            <div className="bp-card-skeleton__line bp-card-skeleton__line--desc-short animate-pulse" />
        </div>
    );
}
